import { Skeleton, Text } from "@prismicio/editor-ui"

import type { QuotaOrLimit } from "../../app/services/billing"
import { useSubscriptionQuery } from "../billing/useSubscription"
import { usageSummaryItemTypes } from "./usageSummaryItemTypes"
import { formatValue, type Unit } from "./usageUtils"

interface UsageSummaryItemDescriptionProps {
  itemTypeKey: keyof typeof usageSummaryItemTypes
  planLimitsKnown: boolean
}

export function UsageSummaryItemDescription(
  props: UsageSummaryItemDescriptionProps,
) {
  const { itemTypeKey, planLimitsKnown } = props

  const { data: subscription } = useSubscriptionQuery()

  if (!planLimitsKnown) return null

  const itemType = usageSummaryItemTypes[itemTypeKey]
  const quota = itemType.getQuota(subscription)
  const limit = itemType.getLimit(subscription)

  return (
    <Text color="grey11">
      {getDescription({ limit, quota, unit: itemType.unit })}
    </Text>
  )
}

interface GetDescriptionArgs {
  limit: QuotaOrLimit
  quota: QuotaOrLimit
  unit: Unit
}

function getDescription(args: GetDescriptionArgs) {
  const { limit, quota, unit } = args

  if (limit === "unlimited" || quota === "unlimited") {
    return "Plan with custom limits"
  }

  const humanReadableQuota = formatValue(quota, { unit })

  if (limit === quota) return `${humanReadableQuota} included`

  const humanReadableLimit = formatValue(limit, { unit })

  return `${humanReadableQuota} included (paid overages allowed up to ${humanReadableLimit})`
}

interface UsageSummaryItemDescriptionSkeletonProps {
  planLimitsKnown?: boolean
}

export function UsageSummaryItemDescriptionSkeleton(
  props: UsageSummaryItemDescriptionSkeletonProps,
) {
  const { planLimitsKnown = true } = props
  return planLimitsKnown ? <Skeleton width={352} height={24} /> : null
}
